import Button from '@/components/Button';
import CurrencySelectBox from '@/components/CurrencySelectBox';
import ToggleSwitch from '@/components/ToggleSwitch';
import { Retailer, RetailerCreate, RetailerUpdate } from '@/models/Retailer';
import OrganizationSelectBox from '@/modules/admin/components/OrganizationSelectBox';
import { CheckIcon } from '@heroicons/react/24/solid';
import { Controller, useForm } from 'react-hook-form';

interface RetailerFormProps {
  retailer?: Retailer;
  onSubmit: (retailer: RetailerCreate | RetailerUpdate) => Promise<void>;
}

const RetailerForm = ({ retailer, onSubmit }: RetailerFormProps) => {
  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
  } = useForm<RetailerCreate | RetailerUpdate>({
    defaultValues: retailer
      ? { ...retailer }
      : {
          organizationId: '',
          name: '',
          email: '',
          currencyCode: '',
          status: 'ACTIVE',
          externalCode: '',
        },
  });

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="grid grid-cols-2 gap-4 lg:gap-8">
        <div className="col-span-2">
          <label htmlFor="name" className="block font-medium text-gray-700">
            Name:
          </label>
          <input
            type="text"
            id="name"
            {...register('name')}
            className="mt-1 p-2 border border-gray-300 rounded-md w-full"
          />
        </div>

        <div className="col-span-2">
          <label htmlFor="email" className="block font-medium text-gray-700">
            Email:
          </label>
          <input
            type="email"
            id="email"
            {...register('email', {
              required: 'Email is required',
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: 'Invalid email address',
              },
            })}
            className="mt-1 p-2 border border-gray-300 rounded-md w-full"
          />
          {errors.email && <span className="text-red-500">{errors.email.message}</span>}
        </div>

        <div className="col-span-2">
          <Controller
            name="organizationId"
            control={control}
            render={({ field }) => (
              <OrganizationSelectBox
                value={field.value}
                onSelect={(org) => field.onChange(org?.id || '')}
              />
            )}
          />
        </div>

        <div className="col-span-2">
          <Controller
            name="currencyCode"
            control={control}
            rules={{ required: 'Currency is required' }}
            render={({ field }) => (
              <CurrencySelectBox
                value={field.value}
                onSelect={(currency) => field.onChange(currency?.id || '')}
                requireSelection
              />
            )}
          />
          {errors.currencyCode && (
            <span className="text-red-500">{errors.currencyCode.message}</span>
          )}
        </div>

        <div className="col-span-2">
          <Controller
            name="status"
            control={control}
            render={({ field }) => (
              <ToggleSwitch
                checked={field.value === 'ACTIVE'}
                onChange={(checked) => field.onChange(checked ? 'ACTIVE' : 'INACTIVE')}
                labelText="Status:"
              />
            )}
          />
        </div>
      </div>

      <div className="mt-4 flex justify-end">
        <Button
          type="submit"
          icon={<CheckIcon className="h-6 w-6" aria-hidden="true" />}
          loading={isSubmitting}
          loadingIconClassName="h-5 w-5 mr-1"
          variant="primary"
        >
          Save
        </Button>
      </div>
    </form>
  );
};

export default RetailerForm;
